import { httpClient } from '@/shell';
import { withIdempotency } from '@neon-one/auctions-types';
import type {
  CancelSaleResult,
  ChargeResult,
  DcfCardBrand,
  FeeCoverageInput,
  FeeCoveragePreview,
  ItemCustodyHistory,
  LotRef,
  ReassignItemRequest,
  ReassignItemResult,
  ReassignLotRequest,
  ReassignTargetsResponse,
  RegenerateReceiptResponse,
  Terminal,
  TerminalCancelResult,
  TerminalChargeInput,
  TerminalListResponse,
  TerminalStatusResult,
} from '@neon-one/auctions-types';
import type {
  AuditLogEntry,
  OfflineMethod,
  RefundAndReleaseResult,
  ReminderSchedule,
} from './types';

// Where a lot's own admin routes live. Items and bundles have sibling routes
// with identical shapes, so every per-lot call builds its path here rather than
// assuming `items/`.
export function lotPath(lot: LotRef): string {
  return lot.kind === 'bundle' ? `bundles/${lot.id}` : `items/${lot.id}`;
}

export const settlementApi = {
  /**
   * The console payload: the invoice roster PLUS the three companion arrays the
   * attention feed reads (`api-contracts.md` §4.3). One request, because the
   * companions are the same read of the same event — a second call could
   * disagree with the roster it is rendered beside.
   *
   * Returns the raw payload; the store normalizes it, so a caller (or a test
   * double) handing back a bare invoice array stays valid and simply carries no
   * companions.
   */
  async getSettlement(auctionUuid: string) {
    const r = await httpClient().get(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/settlement`,
    );
    // httpClient unwraps the envelope to r.data = { items, … }. Tolerate a bare
    // array too (matches close-out's guard).
    return r.data;
  },

  async getInvoice(invoiceUuid: string) {
    const r = await httpClient().get(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}`,
    );
    return r.data;
  },

  // `cardBrand` is the brand the displayed fee was priced with, so the
  // authoritative recompute on the charge reproduces that number. Only a freshly
  // keyed card has one to send: it has no bidder_payment_methods row for the
  // server to read the brand off, so omitting it bills an Amex at the card base
  // rate — above the figure the operator was shown. Send nothing for a stored
  // card; the server reads the row, and asserting a brand here (including
  // 'unknown') suppresses that lookup.
  async charge(
    invoiceUuid: string,
    key: string,
    paymentMethodToken?: string,
    paymentMethodType = 'card',
    cardBrand?: DcfCardBrand,
  ): Promise<ChargeResult> {
    const body = {
      idempotencyKey: key,
      paymentMethodType,
      ...(paymentMethodToken ? { paymentMethodToken } : {}),
      ...(cardBrand ? { cardBrand } : {}),
    };
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/charge`,
      body,
      withIdempotency(key),
    );
    return r.data as ChargeResult;
  },

  // Card-terminal payments. listTerminals returns the org's active
  // devices (empty array when no terminal-capable merchant is configured) plus
  // the resolved merchant's sandbox flag (drives the "simulate tap" dev
  // controls). terminalCharge starts an async tap: the invoice lands in
  // 'processing' and settles to 'paid'/'failed' — the caller polls
  // terminalStatus, which asks Neon Pay rather than waiting on the webhook.
  async listTerminals(auctionUuid: string): Promise<{ terminals: Terminal[]; sandbox: boolean }> {
    const r = await httpClient().get<TerminalListResponse | Terminal[]>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/terminals`,
    );
    return Array.isArray(r.data)
      ? { terminals: r.data, sandbox: false }
      : { terminals: r.data.terminals, sandbox: r.data.sandbox ?? false };
  },

  async terminalCharge(invoiceUuid: string, input: TerminalChargeInput): Promise<ChargeResult> {
    const r = await httpClient().post<ChargeResult>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/terminal-charge`,
      input,
      withIdempotency(input.idempotencyKey),
    );
    return r.data;
  },

  // Reads the pending tap's outcome from Neon Pay and applies it. A POST because
  // it can settle the invoice; safe to repeat, since the apply is idempotent.
  async terminalStatus(invoiceUuid: string): Promise<TerminalStatusResult> {
    const r = await httpClient().post<TerminalStatusResult>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/terminal-status`,
      {},
    );
    return r.data;
  },

  // Resets a 'processing' invoice back to 'pending'. May 409 (CHARGE_CONFLICT /
  // TERMINAL_CANCEL_FAILED) when the tap already completed — the caller treats
  // that as "keep waiting / refresh" rather than a hard failure.
  async terminalCancel(invoiceUuid: string): Promise<TerminalCancelResult> {
    const r = await httpClient().post<TerminalCancelResult>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/terminal-cancel`,
      {},
    );
    return r.data;
  },

  async recordOffline(
    invoiceUuid: string,
    key: string,
    { amountCents, method, reference }: { amountCents: number; method: OfflineMethod; reference?: string },
  ) {
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/offline-payment`,
      { amountCents, paymentMethod: method, ...(reference ? { reference } : {}) },
      withIdempotency(key),
    );
    return r.data;
  },

  // ADR-014 — the settlement surfaces' release control. ONE operation: refund
  // exactly what was collected on each line, then release the lot off the
  // invoice, with a durable obligation so a release never commits without a
  // record of the money owed against it. Unpaid lines refund zero and still
  // release; a line already refunded in full refunds zero and still releases.
  //
  // The body carries no amount on purpose: the server refunds each line against
  // its own remaining balance, so a client figure could only ever disagree with
  // it. /* §3.1 refund-and-release */
  async refundAndRelease(
    invoiceUuid: string,
    key: string,
    lineItemUuids: string[],
    reason: string,
  ): Promise<RefundAndReleaseResult> {
    const r = await httpClient().post<RefundAndReleaseResult>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/refund-and-release`,
      { lineItemUuids, reason },
      withIdempotency(key),
    );
    return r.data;
  },

  // MONEY-ONLY correction path — full or partial, and it does NOT release
  // custody: every line stays live on the invoice. Supervisor-only on the
  // server (GT1-586); its one caller is the drawer's supervisor support refund.
  // Anything that takes a lot OFF an invoice calls `refundAndRelease` above;
  // calling this one from a custody-moving surface is how a
  // refunded-but-unreleased line gets created (AC-4).
  async refund(
    invoiceUuid: string,
    key: string,
    amountCents: number,
    reason: string,
    // Public line-item ids (uuids) this refund covers — passed straight to the
    // backend, which records the refunded amount per line. Never numeric ids.
    lineItemUuids?: string[],
  ) {
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/refund`,
      { amountCents, reason, ...(lineItemUuids && lineItemUuids.length > 0 ? { lineItemUuids } : {}) },
      withIdempotency(key),
    );
    return r.data;
  },

  async declineItems(invoiceUuid: string, lineItemPublicIds: string[]) {
    const r = await httpClient().patch(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/decline-items`,
      { lineItemPublicIds },
    );
    return r.data;
  },

  // The unified reassignment action. One transaction: cancels any
  // pending offer on the item, then either creates the target's offer
  // (mode 'offer') or rewrites the winner and issues the new invoice
  // (mode 'now'), with the notification fan-out gated on `notify`.
  //
  // The Idempotency-Key is REQUIRED here, unlike most POSTs on this file: the
  // key covers the whole action INCLUDING the notify choice, so a retry must
  // carry the key of the submit it is retrying — a fresh key would commit a
  // second, genuinely different reassignment. Callers own key lifetime.
  //
  // Throws ApiError 409 when the item moved underneath the caller (design
  // §edge 5) and 422 when the move-to-next-bidder flag is off.
  async reassign(
    invoiceUuid: string,
    key: string,
    body: ReassignItemRequest,
  ): Promise<ReassignItemResult> {
    const r = await httpClient().post<ReassignItemResult>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/reassign`,
      body,
      withIdempotency(key),
    );
    return r.data;
  },

    async placeLot(
        auctionUuid: string,
        key: string,
        body: ReassignLotRequest,
    ): Promise<ReassignItemResult> {
        const response = await httpClient().post<ReassignItemResult>(
            `/admin/auctions/${encodeURIComponent(auctionUuid)}/lots/reassign`,
            body,
            withIdempotency(key),
        );
        return response.data;
    },

    async fetchPlacementTargets(auctionUuid: string, lot: LotRef): Promise<ReassignTargetsResponse> {
        const response = await httpClient().get<ReassignTargetsResponse>(
            `/admin/auctions/${encodeURIComponent(auctionUuid)}/lots/${lot.kind}/${lot.id}/reassign-targets`,
        );
        return response.data;
    },

  // ADR-012 §7 — who this lot can go to, and where it would land for each of
  // them. NO candidate is ever blocked by invoice state (AC-1): the response
  // varies only in `consequence`, which is the sentence the dialog renders
  // instead of the refusal it used to.
  //
  // Server-authoritative on purpose. The same open-set predicate that decides
  // `appends` vs `new_invoice` is the one the reassign transaction runs, so the
  // preview cannot disagree with the placement the way a client derivation over
  // a settlement payload would once that payload went stale.
  // /* §5 consequence preview */
  async fetchReassignTargets(
    auctionUuid: string,
    lot: LotRef,
  ): Promise<ReassignTargetsResponse> {
    const r = await httpClient().get<ReassignTargetsResponse>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/${lotPath(lot)}/reassign-targets`,
    );
    return r.data;
  },

  // Cancel the sale: declines the winner's line so nothing is owed and sets
  // the lot to `unsold`. The terminal resolution for a lot with no eligible
  // bidder to reassign to. Addressed by the lot's numeric id (the route's
  // contract), not by the invoice line.
  async cancelSale(
    auctionUuid: string,
    lot: LotRef,
    key: string,
    notify: boolean,
  ): Promise<CancelSaleResult> {
    const r = await httpClient().post<CancelSaleResult>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/${lotPath(lot)}/unsold`,
      { notify },
      withIdempotency(key),
    );
    return r.data;
  },

  // ADR-015 §5 — one lot's custody trail plus its current disposition. The
  // events arrive ORDERED, oldest first, with no actor attribution; the client
  // renders them as given and must not sort. Addressed by the lot's numeric id
  // like every other admin item and bundle route.
  //
  // Deliberately not the audit log: that is reverse-chronological, has no item
  // filter, and carries an actor on every row — three reasons it cannot answer
  // "where has this lot been?".
  async fetchItemCustody(auctionUuid: string, lot: LotRef): Promise<ItemCustodyHistory> {
    const r = await httpClient().get<ItemCustodyHistory>(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/${lotPath(lot)}/history`,
    );
    return r.data;
  },

  // Withdraw a pending offer; the item returns to needing attention.
  async cancelOffer(offerUuid: string): Promise<void> {
    await httpClient().post(`/admin/offers/${encodeURIComponent(offerUuid)}/cancel`, {});
  },

  // Charge the bidder's card on file (server looks up the stored Neon Pay
  // token — no client token needed).
  // Typed, because the caller now reads the OUTCOME off this response rather
  // than off a later roster refetch — see store.collect.
  async chargeOnFile(invoiceUuid: string, key: string): Promise<ChargeResult> {
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/charge-on-file`,
      {},
      withIdempotency(key),
    );
    return r.data as ChargeResult;
  },

  // Add-ons (pre-payment only). Both return the updated invoice with the
  // recomputed totalCents.
  async setDonation(invoiceUuid: string, donationCents: number) {
    const r = await httpClient().patch(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/donation`,
      { donationCents },
    );
    return r.data;
  },

  // `method` / `cardBrand` are what the fee is priced for: the donor-covered fee
  // is per-rail and, for cards, per-brand. Omitting them makes the
  // server fall back to the card base rate, so send them whenever the rail is
  // known — otherwise the drawer shows one number and the charge bills another.
  /**
   * Every rail's donor-covered fee for this invoice, priced against its current
   * subtotal + donation. Read-only server-side, so it can be asked for an
   * invoice in any payment status and with coverage switched off — which is the
   * point: the operator has to be able to quote the fee before the bidder has
   * agreed to cover it.
   *
   * `baseCents` is the gross-up base the quotes were computed on. Compare it
   * against the invoice's own subtotal + donation to know whether a donation
   * edit has outrun the matrix.
   */
  async getFeePreview(invoiceUuid: string): Promise<FeeCoveragePreview> {
    const r = await httpClient().get<FeeCoveragePreview>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/fee-preview`,
    );
    return r.data;
  },

  async setFeeCoverage(invoiceUuid: string, input: FeeCoverageInput) {
    const r = await httpClient().patch(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/fee-coverage`,
      {
        enabled: input.enabled,
        ...(input.method ? { method: input.method } : {}),
        ...(input.cardBrand ? { cardBrand: input.cardBrand } : {}),
      },
    );
    return r.data;
  },

  async sendCheckoutEmails(auctionUuid: string): Promise<{ sent: number }> {
    const r = await httpClient().post(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/send-checkout-emails`,
      {},
    );
    return r.data;
  },

  async sendReminders(auctionUuid: string): Promise<{ enqueued: number }> {
    const r = await httpClient().post(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/reminders/send`,
      { kind: 'payment_followup' },
    );
    return r.data;
  },

  async fetchAuditLog(auctionUuid: string, limit = 50): Promise<AuditLogEntry[]> {
    const r = await httpClient().get(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/audit-log?limit=${limit}`,
    );
    // API returns { items: AuditLogEntry[], pageInfo: {...} }
    const data = r.data as { items?: AuditLogEntry[] } | AuditLogEntry[];
    return Array.isArray(data) ? data : (data.items ?? []);
  },

  async sendInvoiceReceipt(invoiceUuid: string): Promise<{ enqueued: number }> {
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/receipts/send`,
      {},
    );
    return r.data;
  },

  async generateReceipt(invoiceUuid: string): Promise<{ receiptPdfUrl?: string; alreadyExists?: boolean; enqueued?: boolean }> {
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/receipts/generate`,
      {},
    );
    return r.data;
  },

  async regenerateReceipt(invoiceUuid: string): Promise<RegenerateReceiptResponse> {
    const r = await httpClient().post<RegenerateReceiptResponse>(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/receipts/regenerate`,
      {},
    );
    return r.data;
  },

  async resendCheckoutLink(invoiceUuid: string): Promise<{ sent: boolean }> {
    const r = await httpClient().post(
      `/admin/invoices/${encodeURIComponent(invoiceUuid)}/resend-checkout`,
      {},
    );
    return r.data;
  },

  async getReminderSchedule(auctionUuid: string): Promise<ReminderSchedule | null> {
    const r = await httpClient().get(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/reminder-schedule`,
    );
    const data = r.data as { schedule?: ReminderSchedule | null };
    return data.schedule ?? null;
  },

  async setReminderSchedule(
    auctionUuid: string,
    channel: 'email' | 'sms' | 'both',
    cadence: 'off' | 'daily' | '2days' | 'weekly',
  ): Promise<ReminderSchedule | null> {
    const r = await httpClient().put(
      `/admin/auctions/${encodeURIComponent(auctionUuid)}/reminder-schedule`,
      { channel, cadence },
    );
    const data = r.data as { schedule?: ReminderSchedule | null };
    return data.schedule ?? null;
  },

  // Dev/UAT only — simulates a card tap on a NeonPay sandbox terminal. Triggers
  // the charge.succeeded / charge.failed webhook flow without a physical device.
  async devTerminalPresentment(
    invoiceUuid: string,
    status: 'succeeded' | 'failed',
  ): Promise<void> {
    await httpClient().post('/dev/terminal-presentment', { invoiceUuid, status });
  },
};
